/**
 * @file SubMenu.js
 * @module src/js/components/SubMenu
 * @description
 * **A reusable submenu component for the navigation menu.**
 * - Displays a list of submenu items under a main menu item.
 * - Highlights submenu items on hover.
 * - Supports both internal and external links.
 * - Closes the menu upon selecting a submenu item.
 *
 * @version
 * <p>
 *  2025-11-29: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Add `ExternalLink` icons to submenu items linking to external URLs. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { NewTab } from './UtilityComponents';

export default function SubMenu({
    submenuIndex,
    title,
    hrefs,
    subTitles,
    isSubmenuVisible,
    setIsSubmenuVisible,
    setIsMenuVisible,
    hoverMenuItem,
    spanStyle,
    baseAnchorStyle,
    hoverStyle,
    handleMouseEnterLeaveMenuItem,
    menuIndex
}) {
    const [hoverSubmenuItem, setHoverSubmenuItem] = useState(new Array(hrefs.length).fill(false));

    const handleMouseEnterLeaveSubmenuItem = useCallback((index, isEnter) => {
        setHoverSubmenuItem(prev => {
            const newHover = [...prev];
            newHover[index] = isEnter;
            return newHover;
        });
    }, []);

    return (
        <li className="has-submenu">
            <div
                onClick={() => setIsSubmenuVisible(prev => {
                    const newSubmenuVisible = new Array(prev.length).fill(false);
                    newSubmenuVisible[submenuIndex] = !prev[submenuIndex];
                    return newSubmenuVisible;
                })}
                onMouseEnter={() => handleMouseEnterLeaveMenuItem(menuIndex, true)}
                onMouseLeave={() => handleMouseEnterLeaveMenuItem(menuIndex, false)}
                style={{ ...baseAnchorStyle, ...(hoverMenuItem[menuIndex] ? hoverStyle : {}) }}
            >
                {title}
                <i
                    className={`fa-solid fa-angle-${isSubmenuVisible[submenuIndex] ? 'down' : 'up'}`}
                    style={{ marginLeft: '0.7em' }}
                ></i>
            </div>
            <ul className={`submenu-${isSubmenuVisible[submenuIndex] ? 'open' : 'close'}`}>
                {hrefs.map((href, hrefIndex) => 
                    <li key={hrefIndex}>
                        {href.startsWith("/") ? (
                            <Link to={href} // Internal route (React Router)
                                onClick={() => {
                                    setIsMenuVisible(false);
                                    setIsSubmenuVisible(prev => new Array(prev.length).fill(false));
                                }}
                                onMouseEnter={() => handleMouseEnterLeaveSubmenuItem(hrefIndex, true)}
                                onMouseLeave={() => handleMouseEnterLeaveSubmenuItem(hrefIndex, false)}
                                style={{ ...baseAnchorStyle, ...(hoverSubmenuItem[hrefIndex] ? hoverStyle : {}) }}
                            >{subTitles[hrefIndex]}</Link>
                        ) : (
                            <NewTab href={href} // External route
                                onClick={() => {
                                    setIsMenuVisible(false);
                                    setIsSubmenuVisible(prev => new Array(prev.length).fill(false));
                                }}
                                onMouseEnter={() => handleMouseEnterLeaveSubmenuItem(hrefIndex, true)}
                                onMouseLeave={() => handleMouseEnterLeaveSubmenuItem(hrefIndex, false)}
                                spanStyle={spanStyle}
                                anchorStyle={{ ...baseAnchorStyle, ...(hoverSubmenuItem[hrefIndex] ? hoverStyle : {}) }}
                            >{subTitles[hrefIndex]}</NewTab>
                        )}
                    </li>
                )}
            </ul>
        </li>
    );
}